import { Button } from '@unistocks/components/button/button';
import { CalendarSize, CELL_FONT, CELL_WIDTH } from '@unistocks/components/calendar/cell-metrics';
import { DateStringValue } from '@unistocks/components/calendar/types';
import { controlA11yProps } from '@unistocks/components/calendar/utils/a11y';
import { DatesProviderContext } from '@unistocks/components/date-picker/date-picker-context';
import { Text } from '@unistocks/components/text/text';
import { createDate, today } from '@unistocks/shared/utils/util';
import { DateTime } from 'luxon';
import { useContext } from 'react';
import { createStyledContext, type GetProps, styled, withStaticProperties } from 'tamagui';
import { webCursor } from '../../../shared/utils/web-cursor';

/** Day cell size — the kit's shared control size scale. */
type DaySize = CalendarSize;

/** Render the day's content (defaults to the day-of-month number). */
export type RenderDay = (date: DateStringValue) => React.ReactNode;

/**
 * Square day-cell metrics shared down to the label via context: each `size` key
 * maps to a square `width`/`height` (from the shared `CELL_WIDTH` ladder), and
 * the label to a proportional `fontSize` (`CELL_FONT`, ≈ size / 2.8, matching
 * @mantine/dates). The numeric ladders live in one place — `../cell-metrics` —
 * so every grid component (Day, WeekdaysRow, Month, PickerControl) stays aligned.
 */
// `minHeight` mirrors `height` at every size so it acts as a FLOOR: when
// `fullWidth` swaps `height` to `"100%"` to fill a constrained calendar height,
// an UNCONSTRAINED parent (no calendar height set) resolves that percentage to
// "auto" and the cell would otherwise collapse to its label's line-height — the
// `minHeight` keeps it at the square size in that case.
const daySizeVariant = {
  xxs: { width: CELL_WIDTH.xxs, height: CELL_WIDTH.xxs, minHeight: CELL_WIDTH.xxs },
  xs: { width: CELL_WIDTH.xs, height: CELL_WIDTH.xs, minHeight: CELL_WIDTH.xs },
  sm: { width: CELL_WIDTH.sm, height: CELL_WIDTH.sm, minHeight: CELL_WIDTH.sm },
  md: { width: CELL_WIDTH.md, height: CELL_WIDTH.md, minHeight: CELL_WIDTH.md },
  lg: { width: CELL_WIDTH.lg, height: CELL_WIDTH.lg, minHeight: CELL_WIDTH.lg },
  xl: { width: CELL_WIDTH.xl, height: CELL_WIDTH.xl, minHeight: CELL_WIDTH.xl },
  xxl: { width: CELL_WIDTH.xxl, height: CELL_WIDTH.xxl, minHeight: CELL_WIDTH.xxl }
} as const;

const dayFontVariant = {
  xxs: { fontSize: CELL_FONT.xxs },
  xs: { fontSize: CELL_FONT.xs },
  sm: { fontSize: CELL_FONT.sm },
  md: { fontSize: CELL_FONT.md },
  lg: { fontSize: CELL_FONT.lg },
  xl: { fontSize: CELL_FONT.xl },
  xxl: { fontSize: CELL_FONT.xxl }
} as const;

const DayContext = createStyledContext<{ size: DaySize }>({ size: 'md' });

/**
 * Whether `day` falls on today's LOCAL calendar date, compared without
 * allocating a single extra dayjs instance.
 *
 * `day.isSame(new Date(), "day")` — the obvious spelling — costs a `Date`, a
 * dayjs instance wrapping it, and two more clones for dayjs' internal
 * `startOf`/`endOf` bracket, i.e. ~4 allocations PER CELL (×42 cells, every
 * render). Reading the three calendar fields off the instance we already have
 * is exact (`year`/`month`/`date` are plain field reads, no clone) and costs
 * only the one `Date` for "now".
 */
function isSameDayAsToday(day: DateTime): boolean {
  const now = today();
  return day.day === now.day && day.month === now.month && day.year === now.year;
}

/**
 * The atomic calendar cell — an `UnstyledButton`-based control composed via
 * `styled`. Accent comes from the active theme's palette ramp via the `theme`
 * prop (no Mantine `color` prop): a selected day fills with `$color9`/`$color1`
 * text; in-range days take a `$color4` tint with the range edges rounded by the
 * `firstInRange`/`lastInRange` corner variants (the `inRange` base squares all
 * four corners, the edge variants round their own two — so a single-day range
 * gets all four back). States use `hoverStyle`/`pressStyle` + a `disabled`
 * variant, never a runtime hover flag or a `Pressable`.
 *
 * Variant precedence relies on Tamagui applying active variants in declaration
 * order (later wins): `inRange` is declared before `firstInRange`/`lastInRange`
 * so the edges win on radius, and before `selected` so a selected endpoint wins
 * on background.
 */
const DayFrame = styled(Button, {
  // No Name, keeps the Button theme
  // name: 'Day',
  context: DayContext,
  chromeless: true,
  transition: null,
  alignItems: 'center',
  justifyContent: 'center',
  borderWidth: 0,
  borderColor: 'transparent',
  backgroundColor: 'transparent',
  ...webCursor('pointer'),

  hoverStyle: {
    borderRadius: '$10'
  },

  variants: {
    size: daySizeVariant,

    /**
     * Day stretches across its grid cell width. Its height stays the square
     * `size` height (with `minHeight` as the floor) — `height: "100%"` would make
     * the cell resolve a percentage against the bounded sheet on native and
     * expand the day vertically.
     */
    fullWidth: {
      true: { width: '100%' }
    },

    /** Day outside the displayed month — dimmed. */
    outside: {
      true: { opacity: 0.5 }
    },

    /** Weekend day — coloured via the label (no frame style needed). */
    weekend: {
      true: {}
    },

    /** Day within a selected range — tinted background, squared corners. */
    inRange: {
      true: {
        backgroundColor: '$color4',
        borderRadius: 0,
        hoverStyle: { borderRadius: 0, backgroundColor: '$color5' },
        pressStyle: { backgroundColor: '$color5' }
      }
    },

    /** Today's border highlight (only when not selected / in range). */
    today: {
      true: { borderWidth: 1, borderRadius: '$10', borderColor: '$color7' }
    },

    /** Selected day — solid accent fill. */
    selected: {
      true: {
        backgroundColor: '$unistocks',
        borderRadius: '$10'
      }
    },

    /**
     * First day of a range — solid accent fill with the inline-start corners
     * rounded and the inline-end corners squared. Declared AFTER `selected` (and
     * `inRange`) so it wins the corner radius: `selected`'s full pill radius
     * (`$10`) and `inRange`'s `borderRadius: 0` are both overridden here — the
     * explicit `borderRadius: 0` squares every corner, then the start pair is
     * rounded. Without this a range's first pick/edge renders as a fully rounded
     * pill instead of a half-rounded edge.
     */
    firstInRange: {
      true: {
        bg: '$unistocks',
        borderRadius: 0,
        borderTopLeftRadius: '$6',
        borderBottomLeftRadius: '$6',
        borderTopRightRadius: 0,
        borderBottomRightRadius: 0,

        hoverStyle: {
          borderTopLeftRadius: '$6',
          borderBottomLeftRadius: '$6',
          borderTopRightRadius: 0,
          borderBottomRightRadius: 0
        }
      }
    },

    /** Last day of a range — the inline-end mirror of `firstInRange`. */
    lastInRange: {
      true: {
        bg: '$unistocks',
        borderRadius: 0,
        borderTopRightRadius: '$6',
        borderBottomRightRadius: '$6',
        borderTopLeftRadius: 0,
        borderBottomLeftRadius: 0,

        hoverStyle: {
          borderTopRightRadius: '$6',
          borderBottomRightRadius: '$6',
          borderTopLeftRadius: 0,
          borderBottomLeftRadius: 0
        }
      }
    },

    /**
     * Hidden from view + the a11y tree but keeps its layout slot. Not
     * `display: 'none'` (collapses the cell to 0 width in `Month`'s flex rows,
     * shifting the following days) and not `visibility: 'hidden'` (not a valid
     * Tamagui style prop — dropped at runtime). `opacity: 0` + `pointerEvents:
     * 'none'` hides the cell while it keeps its grid slot on web AND native.
     */
    hidden: {
      true: {
        opacity: 0,
        pointerEvents: 'none'
      }
    },

    /** Non-interactive cursor/selection + dimmed, no hover affordance. */
    disabled: {
      true: {
        opacity: 0.5,
        pointerEvents: 'none',
        ...webCursor('not-allowed')
      }
    },

    /** Static (non-interactive) display cell — renders as a `div`, no pointer. */
    static: {
      true: {
        userSelect: 'auto',
        ...webCursor('default'),
        hoverStyle: { backgroundColor: 'transparent' },
        pressStyle: { backgroundColor: 'transparent' }
      }
    }
  } as const,

  defaultVariants: {
    size: 'md'
  }
});

/**
 * The day-of-month label — shares `size` via context.
 *
 * Colour is expressed as boolean VARIANTS, never a per-render dynamic `color`
 * style prop: the idle label is the neutral `$color`, a `weekend` label flips to
 * `$red9`, and on the solid accent fill a `selected` label flips to the contrast
 * `$color1`. `selected` is declared after `weekend` so a selected weekend takes
 * the contrast colour (the variants apply in declaration order — later wins,
 * mirroring `DayFrame`). Keeping colour in variants — not a runtime ternary —
 * keeps the cell compiler-safe (no dynamic style prop for the optimiser to fold).
 */
const DayLabel = styled(Text, {
  name: 'DayLabel',
  context: DayContext,
  userSelect: 'none',
  color: '$bodyFont',

  '$group-button-hover': {
    color: '$color'
  },

  '$group-button-press': {
    color: '$color'
  },

  variants: {
    size: dayFontVariant,

    /** Weekend day — accent-error text colour. */
    weekend: {
      true: {
        color: '$red9',

        '$group-button-hover': {
          color: '$red9'
        },

        '$group-button-press': {
          color: '$red9'
        }
      }
    },

    /** Selected day — contrast text on the solid accent fill. */
    selected: {
      true: { color: '$color' }
    }
  } as const,

  defaultVariants: { size: 'md' }
});

type DayFrameProps = GetProps<typeof DayFrame>;

export interface DayProps extends Omit<
  DayFrameProps,
  | 'size'
  | 'outside'
  | 'weekend'
  | 'inRange'
  | 'firstInRange'
  | 'lastInRange'
  | 'today'
  | 'selected'
  | 'hidden'
  | 'disabled'
  | 'static'
  | 'children'
> {
  /** Date displayed in this cell, in `yyyy-MM-dd` format. */
  date: DateStringValue;

  /** Width/height of the day. @default 'md' */
  size?: DaySize;

  /** Render as a non-interactive `div` instead of a button. @default false */
  static?: boolean;

  /** Whether the day is a weekend. @default false */
  weekend?: boolean;

  /** Whether the day is outside of the current month. @default false */
  outside?: boolean;

  /** Whether the day is selected. @default false */
  selected?: boolean;

  /** Whether the day should not be displayed. @default false */
  hidden?: boolean;

  /** Whether the day is within a selected range. @default false */
  inRange?: boolean;

  /** Whether the day is the first in a range selection. @default false */
  firstInRange?: boolean;

  /** Whether the day is the last in a range selection. @default false */
  lastInRange?: boolean;

  /** Custom renderer for the day's content. */
  renderDay?: RenderDay;

  /** Highlight today with a border. @default false */
  highlightToday?: boolean;

  /** Day takes the full width of its cell. @default false */
  fullWidth?: boolean;

  /** Disable the day. @default false */
  disabled?: boolean;
}

const DayComponent = DayFrame.styleable<DayProps>(function Day(props, ref) {
  const {
    date,
    size = 'md',
    static: isStatic = false,
    weekend = false,
    outside = false,
    selected = false,
    hidden = false,
    inRange = false,
    firstInRange = false,
    lastInRange = false,
    renderDay,
    highlightToday = false,
    fullWidth = false,
    disabled = false,
    ...rest
  } = props;

  // The RAW context, not `useDatesContext()`. `Day` needs exactly one value — the
  // locale for its fallback label — and it is the grid's hottest leaf (42
  // instances per month × `numberOfColumns`, all re-rendering on a hover frame).
  // Going through the hook there cost 42 extra hook subscriptions for a single
  // string read. `ctx.locale` below is exactly what `useDatesContext()` returns,
  // so a standalone `<Day>` resolves its locale identically to the rest of the
  // calendar family.
  const ctx = useContext(DatesProviderContext);
  const day = createDate(date);

  // Only the `highlightToday` border consults "is this today", so the comparison
  // is skipped entirely when the border is off (the default).
  const isToday = highlightToday && isSameDayAsToday(day);

  // Locale-aware default label, read from `DatesProvider`. A static (display-only)
  // day announces nothing — it is not an interactive date.
  //
  // An explicit `aria-label` WINS, so it is read up-front and the fallback is
  // only built when there is none: `Month` always passes its own `aria-label`
  // through `...rest` (spread last), which means computing the fallback there
  // was pure waste — a locale clone plus `format("D MMMM YYYY")`, the most
  // expensive token set in the file, for a string that was always discarded,
  // ×42 cells every render. Reading the explicit label here also means NATIVE
  // (`accessibilityLabel` below) now announces the same custom label web does,
  // instead of the generic date it used to fall back to.
  const explicitAriaLabel = props['aria-label'];
  const ariaLabel = isStatic
    ? undefined
    : (explicitAriaLabel ?? day.setLocale(ctx.locale).toFormat('d MMMM yyyy'));

  // Mirror Mantine's data-attribute gating: a disabled day shows none of the
  // selection/range/weekend/outside styling, and today's border is suppressed
  // once the day is selected or in range.
  const isWeekend = !disabled && !outside && weekend;
  const isOutside = !disabled && outside;
  const isSelected = !disabled && selected;
  const isInRange = !disabled && inRange;
  const isFirstInRange = !disabled && firstInRange;
  const isLastInRange = !disabled && lastInRange;
  const showToday = isToday && highlightToday && !isSelected && !isInRange && !disabled;

  const content = renderDay ? renderDay(date) : day.day;
  const isTextContent = typeof content === 'string' || typeof content === 'number';

  return (
    <DayFrame
      ref={ref}
      size={size}
      static={isStatic}
      weekend={isWeekend}
      outside={isOutside}
      selected={isSelected}
      inRange={isInRange}
      firstInRange={isFirstInRange}
      lastInRange={isLastInRange}
      today={showToday}
      hidden={hidden}
      fullWidth={fullWidth}
      disabled={disabled}
      role={isStatic ? 'presentation' : 'button'}
      aria-label={ariaLabel}
      aria-selected={isSelected || undefined}
      aria-disabled={disabled || undefined}
      aria-hidden={hidden || undefined}
      // Native (VoiceOver/TalkBack) counterparts of the web `role`/`aria-*` above;
      // a static display cell announces nothing interactive (`role="none"`).
      {...controlA11yProps({
        role: isStatic ? 'none' : 'button',
        label: ariaLabel,
        selected: isSelected,
        disabled
      })}
      {...(isStatic ? { render: 'div' } : {})}
      {...rest}
    >
      {isTextContent ? (
        <DayLabel weekend={isWeekend} selected={isSelected}>
          {content}
        </DayLabel>
      ) : (
        content
      )}
    </DayFrame>
  );
});

DayComponent.displayName = 'Day';

// Public surface: the component plus its styled parts, so consumers can target /
// extend them (`styled(Day.Frame, …)` / `styled(Day.Label, …)`).
export const Day = withStaticProperties(DayComponent, {
  Frame: DayFrame,
  Label: DayLabel
});

export type { DaySize };
